import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Box, Typography, CircularProgress } from "@mui/material";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteHelpDesk,
  getHelpDesk,
  updateHelpDesk,
} from "../../redux/helpdesk/helpdeskSlice";
import { getProfile } from "../../redux/profile/profileSlice";
import HelpDeskDrawer from "./HelpDeskDrawer";
import { formatShortDate } from "../../utils/const";

const columns = [
  { id: "ticket_no", label: "Ticket No", minWidth: 80, align: "left" },
  { id: "created_at", label: "Ticket Date", minWidth: 120, align: "left" },
  { id: "description", label: "Quarry", minWidth: 150, align: "left" },
  { id: "status", label: "Status", minWidth: 150, align: "right" },
];

const statusStyles = {
  new: {
    label: "New",
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
  },
  in_progress: {
    label: "In Progress",
    backgroundColor: "#fff3cd",
    color: "#ff9800",
  },
  hold: {
    label: "Hold",
    backgroundColor: "#ffe0b2",
    color: "#f57c00",
  },
  resolved: {
    label: "Resolved",
    backgroundColor: "#d0f0c0",
    color: "#388e3c",
  },
  closed: {
    label: "Closed",
    backgroundColor: "#e0e0e0", // light gray background
    color: "#616161", // dark gray text
  },
};

export default function HelpDeskTable({ helpdesk }) {
  const [viewData, setViewData] = useState({});
  const [openView, setOpenView] = useState(false);
  const dispatch = useDispatch();
  const helpdeskStatus = useSelector((state) => state.helpdesk?.status);

  useEffect(() => {
    dispatch(getProfile());
  }, [dispatch]);

  const handleClickOpenView = (row) => {
    setOpenView(true);
    setViewData(row);
  };

  const handleCloseView = () => {
    setOpenView(false);
  };

  const handleOnDeleteticket = () => {
    const queryString = viewData?.ticket_no ? [viewData.ticket_no] : [];
    const ticket_ids = `ticket_ids=${queryString.join(",")}`;
    dispatch(deleteHelpDesk(ticket_ids));
  };

  useEffect(() => {
    if (
      helpdeskStatus === "delete_helpdesk_succeeded" ||
      helpdeskStatus === "update_helpdesk_succeeded"
    ) {
      dispatch(getHelpDesk());
      dispatch(getProfile());
      handleCloseView();
    }
  }, [helpdeskStatus]);

  const handleOnClickRating = (formData) => {
    let ticket_ids = viewData?.ticket_no;
    let userData = formData;
    dispatch(updateHelpDesk({ ticket_ids, userData }));
  };

  const handleOnClickReTicket = ({ action, reason }) => {
    const ticket_ids = viewData?.ticket_no;
    const userData = {
      action,
      reason,
    };

    dispatch(updateHelpDesk({ ticket_ids, userData }));
  };

  const handleOnClickRemoveImageIcon = (image) => {
    const queryString = image?.id ? [image.id] : [];
    const ticket_ids = `image_ids=${queryString.join(",")}`;
    dispatch(deleteHelpDesk(ticket_ids));
  };

  return (
    <Box style={{ backgroundColor: "#fff", padding: "0" }}>
      <Paper
        sx={{
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ maxHeight: 640 }}>
          <Table
            stickyHeader
            aria-label="sticky table"
            style={{ borderSpacing: "0 2px" }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{
                      minWidth: column.minWidth,
                      backgroundColor: "#e5e7eb",
                      fontWeight: "400",
                      fontSize: "13px",
                      padding: "10px 14px 10px 10px",
                      fontFamily: "Calibri",
                    }}
                  >
                    <b>{column.label}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {helpdeskStatus === "loading" ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      py={4}
                    >
                      <CircularProgress color="info" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : helpdesk?.length > 0 ? (
                helpdesk.map((row, index) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          style={{
                            fontSize: "13px",
                            backgroundColor: "#f8f9fa",
                            padding: "10px 15px",
                            fontFamily: "Calibri",
                            color: "inherit",
                          }}
                        >
                          {column.format && typeof value === "number" ? (
                            column.format(value)
                          ) : column.id === "ticket_no" ? (
                            <Box
                              variant="contained"
                              // size="large"
                              sx={{
                                cursor: "pointer",
                                textDecoration: "underline",
                                ":hover": {
                                  color: "#0d47a1",
                                },
                              }}
                              onClick={() => handleClickOpenView(row)}
                            >
                              {value}
                            </Box>
                          ) : column.id === "status" ? (
                            <Box sx={{ textAlign: "right" }}>
                              <span
                                style={{
                                  padding: "4px 10px",
                                  backgroundColor:
                                    statusStyles[value]?.backgroundColor ||
                                    "#e0e0e0",
                                  borderRadius: "3px",
                                  color: statusStyles[value]?.color || "#000",
                                }}
                              >
                                {statusStyles[value]?.label || value}
                              </span>

                              <BiDotsVerticalRounded
                                style={{
                                  fontSize: "22px",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleClickOpenView(row)}
                              />
                            </Box>
                          ) : column.id === "created_at" ? (
                            formatShortDate(value)
                          ) : (
                            value
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length + 2} align="center">
                    <Typography
                      variant="h6"
                      style={{
                        color: "#666",
                        fontFamily: "Calibri",
                        padding: "20px",
                      }}
                    >
                      No Ticket Found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <HelpDeskDrawer
        open={openView}
        onClose={handleCloseView}
        data={viewData}
        onDeleteTicket={handleOnDeleteticket}
        onReTicket={handleOnClickReTicket}
        onRateTicket={handleOnClickRating}
        onRemoveImage={handleOnClickRemoveImageIcon}
        helpdeskStatus={helpdeskStatus}
      />
    </Box>
  );
}
